import { Space } from "~/lib/types";
import {
  AggregateScoreOrder,
  AggregateScoreOrderField,
  AggregateScoreType,
  GetAggregateScoresDocument,
  GetCasesCountDocument,
  OrderDirection,
  ScoreRating,
  useGetAggregateScoresQuery,
} from "~/operations";
import { FirewatchLayout } from "../FirewatchLayout";
import { FirewatchList } from "../FirewatchList";
import {
  ASSET_TABLE_INITIAL_PAGE_RANGE,
  FINDINGS_TABLE_INITIAL_PAGE_RANGE,
  INITIAL_PAGE_RANGE,
} from "../pagination";
import { pageNameByType, scoreTypesByType } from "./lib";
import { LoadingFailedPage } from "../loading";
import { useSort } from "~/pages/inventory/hooks/useSort";
import { useFirewatchFilterContext } from "./hooks/useFirewatchFilterContext";
import { useFirewatchPage } from "./hooks/useFirewatchPage";
import { ScopeType, SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";
import {
  AdjustedAggregateScoreType,
  SelectedEntityWithAggregateScoresData,
} from "./types";
import { usePaginate } from "../pagination/usePaginate";
import { setDocumentTitle } from "~/lib/commonUtils";
import { useSelection } from "~/hooks/useSelection";
import { useEffect } from "react";
import { TriageToolbarButtonType } from "~/components/TriageToolbar/types";
import { useTriageToolbar } from "~/providers/triage-toolbar";
import { useExceptionsButtons } from "~/components/exceptions/use-exceptions-buttons";
import { CreateTicketDialog } from "~/pages/tickets/components/CreateTicketDialog";
import { useDialogs } from "@toolpad/core";
import { useTickets } from "~/pages/tickets/hooks";
import { RemoveExceptionsGroupDialog } from "~/components/exceptions/RemoveExceptionsGroupDialog";
import {
  SelectedEntityWithFindingData,
  SetExceptionDialog,
} from "~/components/exceptions/SetExceptionDialog";
import { useExceptions } from "~/components/exceptions/use-exceptions";
import { pluralize } from "~/lib/pluralize";
import { keyBy } from "lodash-es";
import { useCanApplyExceptionMutation } from "~/lib/iam/actions/mondoo/policy/ExtendedHub/ApplyExceptionMutation";
import { useTicketingIntegrations } from "~/pages/tickets/hooks/useTicketingIntegrations";
import { SelectedEntityWithException } from "~/components/exceptions/types";
import { SelectedEntityWithTicketData } from "~/pages/tickets/types";
import { sortAggregateToExceptionEntitiesMrns } from "./lib/getExceptionEntitiesMrns";
import { useSecurityPipelineIntegrations } from "~/pages/integrations/security-pipeline/hooks/useSecurityPipelineIntegrations";
import { CreateSecurityPipelinePrDialog } from "~/pages/integrations/security-pipeline/components/CreatePullRequest/CreateSecurityPipelinePRDialog";

type FirewatchPageProps = {
  space: Space;
  scope: SpaceOrWorkspaceScope | undefined;
  pageType: AdjustedAggregateScoreType;
  docTitle?: string[];
};

const fireWatchInitialPageRange = (
  pageType: FirewatchPageProps["pageType"],
) => {
  if (pageType === AggregateScoreType.Asset) {
    return ASSET_TABLE_INITIAL_PAGE_RANGE;
  }
  // Set the default range for all firewatch lists under the findings subnav
  if (
    [
      "AllSecurityFindings",
      AggregateScoreType.Vulnerability,
      AggregateScoreType.Advisory,
      AggregateScoreType.Check,
    ].includes(pageType)
  ) {
    return FINDINGS_TABLE_INITIAL_PAGE_RANGE;
  }

  return INITIAL_PAGE_RANGE;
};

export function FirewatchPage({
  space,
  scope,
  pageType,
  docTitle = [],
}: FirewatchPageProps) {
  const { isFeatureEnabled } = useFeatureFlags();
  const { minRiskValue, findingMrn, documentType } = useFirewatchPage({
    pageType,
  });
  const dialogs = useDialogs();
  const triageToolbar = useTriageToolbar();
  const paginate = usePaginate({
    defaultPageSize: fireWatchInitialPageRange(pageType).to,
  });
  const baseUrl = paginate.resetCursorUrl(location.href);

  const hasApplyExceptionPermission = useCanApplyExceptionMutation(space);

  const sort = useSort<AggregateScoreOrder>({
    baseUrl,
    defaultSort: {
      field: AggregateScoreOrderField.RiskValue,
      direction: OrderDirection.Asc,
    },
    validFields: [
      AggregateScoreOrderField.RiskValue,
      AggregateScoreOrderField.BlastRadius,
      AggregateScoreOrderField.Title,
    ],
  });

  const scoreTypes = scoreTypesByType(pageType);
  const baseQueryFilters = {
    scoreTypes,
    minRiskValue,
    findingMrn,
  };

  const { filterForm, filterVariables } = useFirewatchFilterContext({
    scopeMrn: scope?.mrn || "",
    paginate,
    pageType,
    baseQueryFilters,
  });

  const { data, error, loading } = useGetAggregateScoresQuery({
    variables: {
      entityMrn: scope?.mrn || space.mrn,
      ...paginate.pageVars,
      orderBy: sort.orderBy,
      filter: {
        ...baseQueryFilters,
        ...filterVariables,
      },
    },
    fetchPolicy: "cache-and-network",
  });

  const {
    handleCreateTicketingIntegrationModalOpen,
    loading: createTicketLoading,
    handleCreateCase,
    hasCreateTicketPermissions,
  } = useTickets({
    createCaseRefetchQueries: [GetCasesCountDocument],
    onTicketCreate: () => setSelectedEntities([]),
  });

  const { hasTicketingIntegration } = useTicketingIntegrations();
  const { hasSecurityPipelineIntegrations } = useSecurityPipelineIntegrations();

  const {
    selectedEntities,
    setSelectedEntities,
    isGroupChecked,
    isGroupIndeterminate,
    onGroupCheckChange,
    handleNodeClick,
    handleNodeChange,
    handleCancelClick,
  } = useSelection<
    SelectedEntityWithFindingData &
      SelectedEntityWithException &
      SelectedEntityWithTicketData &
      SelectedEntityWithAggregateScoresData
  >();

  const handleCreateTicketDialogOpen = () => {
    if (!space || !scope) return;

    dialogs.open(CreateTicketDialog, {
      isCombinedTicket: isFeatureEnabled("Multi-finding ticketing"),
      target: "asset",
      loading: loading || createTicketLoading,
      space: space,
      scope: scope,
      hasCreateCasePermissions: hasCreateTicketPermissions,
      onSave: (data) =>
        handleCreateCase({
          data,
          refetchQueries: [GetAggregateScoresDocument],
        }),
      selectedEntities: selectedEntities.map((entity) => {
        const entityWithData = edgesByMrn[entity.mrn]?.node;

        return {
          mrn: entityWithData?.findingMrn || "",
          scopeMrn: scope?.mrn,
        };
      }),
    });
  };

  const handleCreateRemediationPRDialogOpen = () => {
    if (!space || !scope) return;

    dialogs.open(CreateSecurityPipelinePrDialog, {
      spaceScope: space,
      aggregateScoresEdges: selectedEntities.map(
        (entity) => edgesByMrn[entity.mrn],
      ),
    });
  };

  const handleRemoveExceptionModalOpen = () => {
    dialogs.open(RemoveExceptionsGroupDialog, {
      onRemove: async (params) => {
        await handleRemoveExceptionGroups(params);
        setSelectedEntities([]);
      },
      exceptionMrns: selectedEntities
        .map((entity) => edgesByMrn[entity.mrn]?.node?.exception?.exceptionMrn)
        .filter((mrn): mrn is string => Boolean(mrn)),
    });
  };

  const handleSetExceptionModalOpen = () => {
    dialogs.open(SetExceptionDialog, {
      targetData: {
        targetTitle: `${selectedEntities.length} ${pluralize("finding", selectedEntities.length)}`,
      },
      target: "finding",
      onSave: handleSetException,
      shouldListEntities: true,
      selectedEntities: selectedEntities.map((entity) => {
        const entityWithData = edgesByMrn[entity.mrn]?.node;

        return {
          mrn: entityWithData?.findingMrn || "",
          scopeMrn: scope?.mrn,
          rating: entityWithData?.rating || ScoreRating.Unscored,
          title: entityWithData?.title || "",
        };
      }),
    });
  };

  const { handleRemoveExceptionGroups, handleSetException } = useExceptions({
    onSetException: () => {
      setSelectedEntities([]);
    },
    scopeMrns: [space.mrn],
    ...sortAggregateToExceptionEntitiesMrns({ selectedEntities }),
    refetchQueries: [GetAggregateScoresDocument],
  });

  useEffect(() => {
    if (selectedEntities.length > 0) {
      triageToolbar.show({
        buttons: [
          {
            type: TriageToolbarButtonType.CreateTicket,
            onClick: !hasTicketingIntegration
              ? handleCreateTicketingIntegrationModalOpen
              : handleCreateTicketDialogOpen,
            disabled: selectedEntities.some((e) => {
              return !e.isAffected;
            }),
            tooltip:
              "Mondoo cannot create tickets for findings with zero blast radius.",
          },
          ...(exceptionsButtonsVisibility.SET_EXCEPTION &&
          scope?.type === ScopeType.Space
            ? [
                {
                  type: TriageToolbarButtonType.CreateException,
                  onClick: handleSetExceptionModalOpen,
                  disabled: !exceptionsButtonsAvailability.SET_EXCEPTION,
                  tooltip: exceptionsButtonsTooltipText.SET_EXCEPTION,
                },
              ]
            : []),
          ...(exceptionsButtonsVisibility.REMOVE_EXCEPTION &&
          scope?.type === ScopeType.Space
            ? [
                {
                  type: TriageToolbarButtonType.RemoveException,
                  onClick: handleRemoveExceptionModalOpen,
                  disabled: !exceptionsButtonsAvailability.REMOVE_EXCEPTION,
                  tooltip: exceptionsButtonsTooltipText.REMOVE_EXCEPTION,
                },
              ]
            : []),
          ...(scope?.type === ScopeType.Space && hasSecurityPipelineIntegrations
            ? [
                {
                  type: TriageToolbarButtonType.CreateRemediationWorkflow,
                  onClick: handleCreateRemediationPRDialogOpen,
                  disabled: selectedEntities.some((e) => {
                    return (
                      !e.isAffected ||
                      ![
                        AggregateScoreType.Advisory,
                        AggregateScoreType.Vulnerability,
                      ].includes(e.scoreType)
                    );
                  }),
                  tooltip:
                    "Mondoo cannot create pull requests for check findings or findings with zero blast radius.",
                },
              ]
            : []),
        ],
        payload: {
          count: selectedEntities.length,
          totalCount,
          onClose: handleCancelClick,
        },
      });
    } else {
      triageToolbar.hide();
    }

    return () => {
      triageToolbar.hide();
    };
  }, [selectedEntities, scope]);

  const aggregateScores =
    data?.aggregateScores?.__typename === "AggregateScoresConnection"
      ? data.aggregateScores
      : undefined;

  const paginateProps = paginate.pageProps(aggregateScores?.pageInfo);

  const totalCount = aggregateScores?.totalCount || 0;
  const edges = aggregateScores?.edges || [];
  const noTotal = data && Boolean(totalCount === 0);
  const noEntriesWithFilters = noTotal && Boolean(edges?.length === 0);
  const noEntries = noEntriesWithFilters
    ? "No entries found with the current filter selection."
    : noTotal
      ? "No entries found"
      : undefined;

  const edgesWithMrns = edges.flatMap((edge) =>
    edge.node && scope?.mrn
      ? [
          {
            ...edge.node,
            title: edge.node.title || "",
            rating: edge.node.rating || ScoreRating.Unscored,
            mrn: edge.node.findingMrn,
            exception: edge.node.exception,
            scopeMrn: scope.mrn,
            isAffected: (edge.node?.blastRadius?.affected || 0) > 0,
            scoreType: edge?.node?.scoreType || AggregateScoreType.Check,
          },
        ]
      : [],
  );

  const edgesByMrn = keyBy(edges, "node.findingMrn");

  const {
    exceptionsButtonsAvailability,
    exceptionsButtonsVisibility,
    exceptionsButtonsTooltipText,
  } = useExceptionsButtons({
    selectedEntities: selectedEntities.map((entity) => {
      const entityWithData = edgesByMrn[entity.mrn]?.node;

      return {
        mrn: entityWithData?.findingMrn || "",
        exception: entityWithData?.exception,
      };
    }),
    target: "finding",
    showExceptionButtons: true,
    showScopeButtons: true,
  });

  setDocumentTitle([...docTitle, scope?.name]);

  if (error) {
    return <LoadingFailedPage what={pageNameByType(pageType)} />;
  }

  return (
    <FirewatchLayout
      filterForm={filterForm}
      pageProps={{
        space,
        scope,
        pageType,
        noEntries,
        ...(isFeatureEnabled("Reporting") && documentType && { documentType }),
        views: {
          list: (
            <FirewatchList
              space={space}
              scope={scope}
              pageType={pageType}
              loading={loading && !data}
              data={edges}
              sort={sort}
              paginateProps={paginateProps}
              selectionProps={{
                canSelect:
                  hasApplyExceptionPermission || hasCreateTicketPermissions,
                selectedEntities,
                isGroupChecked: isGroupChecked(edgesWithMrns),
                isGroupIndeterminate: isGroupIndeterminate(edgesWithMrns),
                onAllCheckboxClick: (event) => {
                  onGroupCheckChange(event, edgesWithMrns);
                },
                onRowCheckboxChange: handleNodeChange,
                onRowCheckboxClick: handleNodeClick,
              }}
            />
          ),
        },
      }}
    />
  );
}
